/**
 * Tabs — the canonical horizontal-tab strip. Replaces the inline-style
 * tab buttons across CRM, ChannelStrategy, NewBusiness, ProductDetails,
 * Pricing. Uses the .tab-active / .tab-inactive CSS classes so hover
 * affordances continue to work.
 *
 * Usage:
 *   <Tabs value={tab} onChange={setTab}>
 *     <Tab id="overview" label="Overview" />
 *     <Tab id="contacts" label={`Contacts (${count})`} />
 *   </Tabs>
 *
 * Or with the items prop:
 *   <Tabs
 *     value={tab}
 *     onChange={setTab}
 *     items={[
 *       { id: 'overview', label: 'Overview' },
 *       { id: 'contacts', label: `Contacts (${count})` },
 *     ]}
 *   />
 */
export default function Tabs({ value, onChange, items, children, style }) {
  const tabs = items || extractFromChildren(children);
  return (
    <div
      style={{
        display: 'flex',
        borderBottom: '1px solid var(--border)',
        background: 'var(--bg-elevated)',
        ...style,
      }}
    >
      {tabs.map(t => {
        const active = value === t.id;
        return (
          <button
            key={t.id}
            onClick={() => onChange(t.id)}
            className={active ? 'tab-active' : 'tab-inactive'}
            style={{
              padding: '10px 18px',
              background: 'none',
              borderTop: 'none',
              borderLeft: 'none',
              borderRight: 'none',
              borderBottom: active ? undefined : '2px solid transparent',
              marginBottom: -1,
              cursor: 'pointer',
              fontSize: 13,
              fontWeight: active ? 600 : 500,
              transition: 'color 0.15s, border-color 0.15s',
            }}
          >
            {t.label}
          </button>
        );
      })}
    </div>
  );
}

// Tab is a marker component — Tabs reads its props but never renders it.
export function Tab() {
  return null;
}

function extractFromChildren(children) {
  const arr = Array.isArray(children) ? children : [children];
  return arr
    .filter(c => c && c.props && c.props.id !== undefined)
    .map(c => ({ id: c.props.id, label: c.props.label }));
}
